/** Analytic dimensions (M03-03): branch, department, cost center, project. */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useDimensions } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, StatusChip, inputClass } from '../../components/ui';

type Dialog = { kind: 'dimension' } | { kind: 'value'; dimensionId: string; dimensionName: string } | null;

export function DimensionsPage(): JSX.Element {
  const { can } = useAuth();
  const dimensions = useDimensions();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: ({ path, active }: { path: string; active: boolean }) => api(path, { method: 'PATCH', body: { active } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dimensions'] }),
  });

  const create = useMutation({
    mutationFn: () =>
      dialog?.kind === 'value'
        ? api(`/dimensions/${dialog.dimensionId}/values`, { method: 'POST', body: { code, name } })
        : api('/dimensions', { method: 'POST', body: { code, name } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dimensions'] });
      setDialog(null);
      setCode('');
      setName('');
      setError(null);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not save.'),
  });

  return (
    <>
      <PageHeader
        title="Dimensions"
        description="Tag journal lines by branch, department, cost center or project, then report profit along any of them."
        actions={can('coa.manage') && <Button onClick={() => setDialog({ kind: 'dimension' })}><Plus aria-hidden="true" className="h-4 w-4" /> New dimension</Button>}
      />
      {dimensions.isLoading && <Skeleton className="h-48 w-full" />}
      {dimensions.data?.length === 0 && (
        <Card><EmptyState title="No dimensions yet" description="Add one such as Department with values like Sales and Operations." /></Card>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {dimensions.data?.map((d) => (
          <Card
            key={d.id}
            title={d.name}
            description={d.code}
            actions={
              can('coa.manage') && (
                <Button size="sm" variant="secondary" onClick={() => setDialog({ kind: 'value', dimensionId: d.id, dimensionName: d.name })}>
                  <Plus aria-hidden="true" className="h-4 w-4" /> Value
                </Button>
              )
            }
          >
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {d.values.map((v) => (
                <li key={v.id} className="flex items-center justify-between py-2">
                  <span className={v.active ? '' : 'opacity-50'}><span className="font-medium">{v.code}</span> — {v.name}</span>
                  {can('coa.manage') ? (
                    <button className="text-xs text-brand-700 hover:underline dark:text-brand-100" onClick={() => toggle.mutate({ path: `/dimension-values/${v.id}`, active: !v.active })}>
                      {v.active ? 'Deactivate' : 'Activate'}
                    </button>
                  ) : (
                    !v.active && <StatusChip tone="neutral">Inactive</StatusChip>
                  )}
                </li>
              ))}
              {d.values.length === 0 && <li className="py-2 text-slate-500">No values yet.</li>}
            </ul>
          </Card>
        ))}
      </div>
      {dialog && (
        <Modal title={dialog.kind === 'value' ? `New ${dialog.dimensionName} value` : 'New dimension'} onClose={() => setDialog(null)}>
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            {error && <Alert tone="critical">{error}</Alert>}
            <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
              <Field label="Code" required><input className={inputClass} value={code} maxLength={20} onChange={(e) => setCode(e.target.value)} autoFocus /></Field>
              <Field label="Name" required><input className={inputClass} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} /></Field>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
              <Button type="submit" busy={create.isPending} disabled={!code.trim() || name.trim().length < 2}>Save</Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
